/* Soul Enrichment Loop — review queue (all programmes).
 * Claude distils soul facts from Zoho conversations nightly; every
 * suggestion needs a human Approve before it touches the member's
 * soul profile. */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Check, X, RefreshCw } from 'lucide-react';

const CLIENT_LABELS = {
  jlr: 'JLR', aabe: 'AaBee', xiaomi: 'Xiaomi', redberyl: 'RedBeryl',
  me: 'Mind Escapes', aurelion: 'Aurelion', godrej: 'Godrej', ggc: 'GGC',
  kvb: 'KVB',
};

export default function SoulEnrichmentPage() {
  const [data, setData] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [running, setRunning] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/soul-enrichment/pending${clientFilter ? `?client_id=${clientFilter}` : ''}`);
      setData(r.data);
    } catch (e) {
      toast.error('Could not load suggestions');
    }
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (sid, action) => {
    setBusyId(sid);
    try {
      await api.post(`/soul-enrichment/suggestion/${sid}/${action}`);
      toast.success(action === 'approve' ? 'Merged into the soul profile' : 'Rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally { setBusyId(null); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post(`/soul-enrichment/run-now${clientFilter ? `?client_id=${clientFilter}` : ''}`);
      toast.success('Sweep started — Claude is reading the last 7 days of conversations. Refresh in a minute.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the sweep');
    } finally { setRunning(false); }
  };

  const toggleAuto = async (cid, on) => {
    try {
      await api.post('/soul-enrichment/settings', { client_id: cid, auto_approve: on });
      toast.success(`${CLIENT_LABELS[cid] || cid}: auto-add ${on ? 'ON — new facts merge automatically' : 'OFF — back to manual approval'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };

  const saveEdit = async (sid) => {
    if (!editVal.trim()) return;
    setBusyId(sid);
    try {
      await api.post(`/soul-enrichment/suggestion/${sid}/edit`, { value: editVal.trim() });
      toast.success('Fact updated');
      setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Edit failed'); }
    finally { setBusyId(null); }
  };

  const items = data?.items || [];
  const counts = data?.pending_by_client || {};
  const autoSettings = data?.auto_settings || {};
  const recentAuto = data?.recent_auto || [];

  const EditBox = ({ s }) => editId === s._id ? (
    <div className="mt-2 flex gap-2">
      <input value={editVal} onChange={(e) => setEditVal(e.target.value)}
        className="flex-1 border border-[#d8d4cc] rounded-lg px-3 py-1.5 text-sm"
        data-testid={`soul-edit-input-${s._id}`} autoFocus />
      <button onClick={() => saveEdit(s._id)} disabled={busyId === s._id}
        className="px-3 py-1.5 rounded-lg bg-[#1B2A4A] text-white text-[12px] font-semibold"
        data-testid={`soul-edit-save-${s._id}`}>Save</button>
      <button onClick={() => setEditId(null)}
        className="px-3 py-1.5 rounded-lg border border-[#d8d4cc] text-[12px]">Cancel</button>
    </div>
  ) : null;

  return (
    <div className="p-8 space-y-5 max-w-5xl" data-testid="soul-enrichment-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#1B2A4A', fontFamily: 'Fraunces, serif' }}>
            <Sparkles size={26} color="#7A5C2B" /> Soul Enrichment Loop
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Claude reads real member conversations nightly and suggests new soul
            facts. Nothing touches a profile until you approve it.
            {data?.last_run && (
              <span className="ml-2 text-[12px] text-[#9CA3AF]">
                last sweep: {String(data.last_run.ran_at).slice(0, 16).replace('T', ' ')} UTC
                · {data.last_run.suggestions_created} suggestions
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runNow}
            disabled={running}
            className="px-4 py-2 rounded-lg bg-[#1B2A4A] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="soul-enrichment-run-now-btn"
          >
            <RefreshCw size={14} className={running ? 'animate-spin' : ''} />
            {running ? 'Starting…' : 'Run sweep now'}
          </button>
        </div>
      </div>

      {/* Per-tenant auto-add toggles */}
      <div className="rounded-xl border border-[#e5e2dc] bg-white px-5 py-4" data-testid="soul-enrichment-settings">
        <div className="text-[13px] font-semibold text-[#3B3528] mb-2">
          Auto-add per programme — ON means new facts merge into souls automatically (you can still edit or reject them below)
        </div>
        <div className="flex gap-3 flex-wrap">
          {Object.entries(CLIENT_LABELS).map(([cid, label]) => {
            const on = !!autoSettings[cid];
            return (
              <button key={cid} onClick={() => toggleAuto(cid, !on)}
                data-testid={`soul-auto-toggle-${cid}`}
                className={`px-3.5 py-2 rounded-lg text-[13px] font-semibold border flex items-center gap-2 ${
                  on ? 'bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46]' : 'bg-white border-[#d8d4cc] text-[#6B6B6B]'
                }`}>
                <span className={`h-2.5 w-2.5 rounded-full ${on ? 'bg-[#059669]' : 'bg-[#C9C1AE]'}`} />
                {label}: {on ? 'Auto-add ON' : 'Manual'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Client filter chips */}
      <div className="flex gap-2 flex-wrap" data-testid="soul-enrichment-client-chips">
        <button
          onClick={() => setClientFilter('')}
          className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border ${
            !clientFilter ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#1B2A4A] border-[#d8d4cc]'
          }`}
        >
          All ({data?.total_pending ?? 0})
        </button>
        {Object.entries(CLIENT_LABELS).map(([cid, label]) => (
          <button
            key={cid}
            onClick={() => setClientFilter(cid)}
            data-testid={`soul-enrichment-chip-${cid}`}
            className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border ${
              clientFilter === cid ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#1B2A4A] border-[#d8d4cc]'
            }`}
          >
            {label} ({counts[cid] || 0})
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-[#e5e2dc] bg-white p-8 text-sm text-[#6B6B6B]" data-testid="soul-enrichment-empty">
          ✓ Nothing waiting for review. New suggestions appear here after the
          nightly sweep (09:30 IST) — or hit "Run sweep now".
        </div>
      ) : (
        <div className="space-y-3" data-testid="soul-enrichment-list">
          {items.map((s) => (
            <div
              key={s._id}
              className="rounded-xl border border-[#e5e2dc] bg-white p-5"
              data-testid={`soul-suggestion-${s._id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[15px] text-[#1B2A4A]">{s.member_name}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#f3eee4] text-[#6B5439] font-semibold">
                      {CLIENT_LABELS[s.client_id] || s.client_id}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#eef4fb] text-[#1B3D7A] font-mono">
                      {s.field}
                    </span>
                  </div>
                  <div className="mt-2 text-sm">
                    {s.current_value ? (
                      <div className="text-[13px] text-[#9CA3AF] line-through mb-0.5">{s.current_value}</div>
                    ) : null}
                    <div className="text-[#1B2A4A] font-medium">{s.suggested_value}</div>
                  </div>
                  {s.evidence && (
                    <div className="mt-1.5 text-[12px] italic text-[#7A5C2B]">
                      “{s.evidence}”
                    </div>
                  )}
                  <EditBox s={s} />
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => decide(s._id, 'approve')}
                    disabled={busyId === s._id}
                    className="px-3.5 py-2 rounded-lg bg-[#065f46] text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`soul-suggestion-approve-${s._id}`}
                  >
                    <Check size={14} /> Approve
                  </button>
                  <button
                    onClick={() => { setEditId(s._id); setEditVal(s.suggested_value); }}
                    className="px-3.5 py-2 rounded-lg bg-white border border-[#d8d4cc] text-[#1B2A4A] text-[13px] font-semibold hover:bg-[#f7f5f2]"
                    data-testid={`soul-suggestion-edit-${s._id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => decide(s._id, 'reject')}
                    disabled={busyId === s._id}
                    className="px-3.5 py-2 rounded-lg bg-white border border-[#d8d4cc] text-[#6B6B6B] text-[13px] font-semibold hover:bg-[#f7f5f2] disabled:opacity-50 flex items-center gap-1"
                    data-testid={`soul-suggestion-reject-${s._id}`}
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recently auto-added */}
      {recentAuto.length > 0 && (
        <div className="space-y-2" data-testid="soul-enrichment-auto-list">
          <h2 className="text-[15px] font-semibold text-[#065f46]">
            ✓ Recently auto-added ({recentAuto.length}) — already on the soul; edit anytime
          </h2>
          {recentAuto.map((s) => (
            <div key={s._id} className="rounded-lg border border-[#d9efe4] bg-[#f6fdf9] px-4 py-3"
                 data-testid={`soul-auto-${s._id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm">
                  <span className="font-semibold text-[#1B2A4A]">{s.member_name}</span>
                  <span className="text-[11px] ml-2 px-1.5 py-0.5 rounded bg-[#eef4fb] text-[#1B3D7A] font-mono">{s.field}</span>
                  <span className="text-[11px] ml-1.5 text-[#9CA3AF]">{CLIENT_LABELS[s.client_id] || s.client_id}</span>
                  <div className="text-[#1B2A4A] mt-0.5">{s.merged_value || s.suggested_value}</div>
                  <EditBox s={s} />
                </div>
                <button onClick={() => { setEditId(s._id); setEditVal(s.merged_value || s.suggested_value); }}
                  className="px-3 py-1.5 rounded-lg border border-[#c6e3d4] text-[#065f46] text-[12px] font-semibold flex-shrink-0"
                  data-testid={`soul-auto-edit-${s._id}`}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
